import type { TripSegment } from '../types'

export interface SegmentFilters {
	date_range?: string
	keyword?: string
	order_status?: string
	shipper_ids?: string
	consignee_ids?: string
	is_active?: string
	limit?: number
}

export interface SegmentDebugOptions {
	enabled?: boolean
	traceId?: string
	requestSource?: string
}

function logSegmentDebug(
	debug: SegmentDebugOptions | undefined,
	message: string,
	details?: unknown,
	level: 'error' | 'info' | 'log' | 'warn' = 'log'
) {
	if (!debug?.enabled) return

	const prefix = `[shipments-tracker-embed][${debug.traceId || 'no-trace'}]`
	if (details === undefined) {
		console[level](`${prefix} ${message}`)
		return
	}

	console[level](`${prefix} ${message}`, details)
}

function redactToken(url: URL): string {
	const safeUrl = new URL(url.toString())
	if (safeUrl.searchParams.has('token')) {
		safeUrl.searchParams.set('token', '[redacted]')
	}

	return safeUrl.toString()
}

function summarizeSegments(segments: TripSegment[]) {
	const customers = [...new Set(segments.map((segment) => segment.customer_id).filter(Boolean))]
	const locations = [...new Set(segments.map((segment) => segment.location_id).filter(Boolean))]
	const shippers = [...new Set(segments.map((segment) => segment.shipper_id).filter(Boolean))]

	return {
		count: segments.length,
		customer_ids: customers.slice(0, 5),
		location_ids: locations.slice(0, 5),
		shipper_ids: shippers.slice(0, 5),
		pro_numbers: segments.slice(0, 5).map((segment) => segment.pro_num),
	}
}

export async function fetchSegments(
	token: string,
	apiURL: string,
	filters?: SegmentFilters,
	debug?: SegmentDebugOptions
): Promise<TripSegment[]> {
	const url = new URL(`${apiURL}/api/segments`)
	url.searchParams.set('token', token)

	// Add all filter parameters if provided
	if (filters) {
		if (filters.date_range) url.searchParams.set('date_range', filters.date_range)
		if (filters.keyword) url.searchParams.set('keyword', filters.keyword)
		if (filters.order_status) url.searchParams.set('order_status', filters.order_status)
		if (filters.shipper_ids) url.searchParams.set('shipper_ids', filters.shipper_ids)
		if (filters.consignee_ids) url.searchParams.set('consignee_ids', filters.consignee_ids)
		if (filters.is_active) url.searchParams.set('is_active', filters.is_active)
		if (filters.limit) url.searchParams.set('limit', filters.limit.toString())
	}

	if (debug?.enabled) {
		url.searchParams.set('debug', 'shipments')
		if (debug.traceId) {
			url.searchParams.set('debug_trace', debug.traceId)
		}
	}

	logSegmentDebug(debug, 'Requesting /api/segments', {
		requestSource: debug?.requestSource || 'unknown',
		url: redactToken(url),
		filters: filters || {},
	})

	const res = await fetch(url)
	logSegmentDebug(debug, 'Received /api/segments response', {
		ok: res.ok,
		status: res.status,
		url: redactToken(url),
	})

	if (!res.ok) {
		const errorBody = await res.text()
		logSegmentDebug(
			debug,
			'Segments request failed',
			{
				status: res.status,
				body: errorBody.slice(0, 500),
			},
			'error'
		)
		throw new Error(`API error: ${res.status}`)
	}

	const segments = (await res.json()) as TripSegment[]

	logSegmentDebug(
		debug,
		segments.length === 0 ? 'Segments response was empty' : 'Segments response summary',
		summarizeSegments(segments),
		segments.length === 0 ? 'warn' : 'log'
	)

	return segments
}

export async function fetchPings(
	token: string,
	apiURL: string,
	tripId: string
): Promise<Array<{ lat: number; lng: number; ts: string }>> {
	const url = new URL(`${apiURL}/api/pings/${tripId}`)
	url.searchParams.set('token', token)

	const res = await fetch(url)
	if (!res.ok) throw new Error(`API error: ${res.status}`)

	return res.json()
}

export async function snapRoute(
	token: string,
	apiURL: string,
	tripId: string
): Promise<{ snapped_geojson: GeoJSON.LineString | GeoJSON.MultiLineString | GeoJSON.Point }> {
	const url = new URL(`${apiURL}/api/segments/${tripId}/snap`)
	url.searchParams.set('token', token)

	const res = await fetch(url, { method: 'POST' })
	if (!res.ok) throw new Error(`API error: ${res.status}`)

	return res.json()
}
